import { WebRtcTestHandler } from '../WebRtcTestHandler';
import { testStates } from './Test';

export class DataChannelThroughputTest {
  private readonly testDurationSeconds = 5.0;
  private readonly maxNumberOfPacketsToSend = 1;
  private readonly bytesToKeepBuffered = 1024 * this.maxNumberOfPacketsToSend;
  private readonly timeoutThreshold = 1000 * 60;
  private readonly minBandwidthThreshold = 300.0;

  private startTime: Date | undefined;
  private sentPayloadBytes = 0;
  private receivedPayloadBytes = 0;
  private stopSending = false;
  private readonly samplePacket = '';

  private lastBitrateMeasureTime: Date | undefined;
  private lastReceivedPayloadBytes = 0;

  private senderChannel: RTCDataChannel | undefined;
  private receiveChannel: RTCDataChannel | undefined;

  private rtcPeerConn1: RTCPeerConnection | undefined;
  private rtcPeerConn2: RTCPeerConnection | undefined;

  private timeout: number | undefined;

  public readonly name = 'BandwidthTest';

  public testState = testStates['not started'];

  constructor (
    private readonly rtcConfiguration: RTCConfiguration,
    public readonly testMethod: (candidate: RTCIceCandidate) => boolean,
    private readonly handler: WebRtcTestHandler,
    public readonly resultElement: HTMLLIElement,
    public readonly isCritical: boolean) {
    for (let i = 0; i !== 1024; ++i) {
      this.samplePacket += 'h';
    }
  }

  public async startTest () {
    this.testState = testStates.running;

    try {
      this.rtcPeerConn1 = new RTCPeerConnection(this.rtcConfiguration);
      this.rtcPeerConn2 = new RTCPeerConnection(this.rtcConfiguration);
    } catch {
      this.endTest(false, WebRtcTestHandler.errorMessages.COULD_NOT_CREATE_PEER_CONNECTION);
      return;
    }

    this.rtcPeerConn1.onicecandidate = (evt) => {
      const iceCandidate = evt.candidate;
      if (!iceCandidate) return;
      if (this.testMethod(iceCandidate)) {
        this.rtcPeerConn2?.addIceCandidate(iceCandidate);
      }
    };

    this.rtcPeerConn2.onicecandidate = (evt) => {
      const iceCandidate = evt.candidate;
      if (!iceCandidate) return;
      if (this.testMethod(iceCandidate)) {
        this.rtcPeerConn1?.addIceCandidate(iceCandidate);
      }
    };

    this.senderChannel = this.rtcPeerConn1.createDataChannel('test2');
    this.senderChannel.addEventListener('open', this.sendingStep.bind(this));

    this.rtcPeerConn2.addEventListener('datachannel',
      this.onReceiverChannel.bind(this));

    await this.establishConnection();

    this.timeout = setTimeout(this.endTest.bind(this), this.timeoutThreshold, false, WebRtcTestHandler.errorMessages.TIMEOUT);
  }

  private onReceiverChannel (event: RTCDataChannelEvent) {
    this.receiveChannel = event.channel;
    this.receiveChannel.addEventListener('message',
      this.onMessageReceived.bind(this));
  }

  private sendingStep () {
    const now = new Date();
    if (!this.startTime) {
      this.startTime = now;
      this.lastBitrateMeasureTime = now;
    }

    for (let i = 0; i !== this.maxNumberOfPacketsToSend; ++i) {
      if (this.senderChannel!.bufferedAmount >= this.bytesToKeepBuffered) {
        break;
      }
      this.sentPayloadBytes += this.samplePacket.length;
      this.senderChannel!.send(this.samplePacket);
    }

    if (now.getTime() - this.startTime.getTime() >= 1000 * this.testDurationSeconds) {
      this.stopSending = true;
    } else {
      setTimeout(this.sendingStep.bind(this), 1);
    }
  }

  private onMessageReceived (event: MessageEvent) {
    this.receivedPayloadBytes += event.data.length;
    const now = new Date();
    if (now.getTime() - this.lastBitrateMeasureTime!.getTime() >= 1000) {
      let bitrate = (this.receivedPayloadBytes -
        this.lastReceivedPayloadBytes) / (now.getTime() - this.lastBitrateMeasureTime!.getTime());
      bitrate = Math.round(bitrate * 1000 * 8) / 1000;
      console.log(`Transmitting at ${bitrate} kbps.`);
      this.lastReceivedPayloadBytes = this.receivedPayloadBytes;
      this.lastBitrateMeasureTime = now;
    }
    if (this.stopSending &&
      this.sentPayloadBytes === this.receivedPayloadBytes) {
      const elapsedTime = Math.round((now.getTime() - this.startTime!.getTime()) * 10) / 10000.0;
      const receivedKBits = this.receivedPayloadBytes * 8 / 1000;
      console.log(`Total transmitted: ${receivedKBits} kilo-bits in ${elapsedTime} seconds.`);
      if (receivedKBits / elapsedTime < this.minBandwidthThreshold) {
        this.endTest(false, `Total transmitted: ${receivedKBits} kilo-bits in ${elapsedTime} seconds.`);
      } else {
        this.endTest(true, `Total transmitted: ${receivedKBits} kilo-bits in ${elapsedTime} seconds.`);
      }
    }
  }

  private async establishConnection () {
    if (!(this.rtcPeerConn1 && this.rtcPeerConn2)) {
      this.endTest(false, WebRtcTestHandler.errorMessages.PEER_CONNECTION_NOT_FOUND);
      return;
    }

    try {
      var offer = await this.rtcPeerConn1.createOffer();
    } catch {
      this.endTest(false, WebRtcTestHandler.errorMessages.COULD_NOT_CREATE_OFFER);
      return;
    }

    try {
      await this.rtcPeerConn1.setLocalDescription(offer);
      await this.rtcPeerConn2.setRemoteDescription(offer);
    } catch {
      this.endTest(false, WebRtcTestHandler.errorMessages.COULD_NOT_SET_OFFER_DESCRIPTION);
      return;
    }

    try {
      var answer = await this.rtcPeerConn2.createAnswer();
    } catch {
      this.endTest(false, WebRtcTestHandler.errorMessages.COULD_NOT_CREATE_ANSWER);
      return;
    }

    try {
      await this.rtcPeerConn2.setLocalDescription(answer);
      await this.rtcPeerConn1.setRemoteDescription(answer);
    } catch {
      this.endTest(false, WebRtcTestHandler.errorMessages.COULD_NOT_SET_ANSWER_DESCRIPTION);
    }
  }

  public endTest (successful: boolean, message?: string) {
    successful ? this.testState = testStates.succeded : this.testState = testStates.failed;

    message ? this.handler.displayResult(successful, this, message) : this.handler.displayResult(successful, this);

    clearTimeout(this.timeout);

    this.rtcPeerConn1?.close();
    this.rtcPeerConn2?.close();
    this.receiveChannel?.close();
    this.senderChannel?.close();
  }
}
